import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { LogEventParams } from "@utils/track-event/event-types";
import { IconButton, useToast } from "@flat-ui";

interface Props {
  /** The text to be copied to the clipboard */
  textToCopy: string;

  /** Message to show on successful copy */
  successMessage: string;

  /** Message to show on failed copy */
  failedMessage: string;

  /** Optional name of the event to track when the button is clicked. */
  trackEventName?: LogEventParams;
}

/**
 * Displays a button that copies the provided text to clipboard when clicked.
 * Also shows a toast indicating success or failure of the copy action.
 */
export function CopyTextButton({
  textToCopy,
  successMessage,
  failedMessage,
  trackEventName,
}: Props): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();

  /** Copies text to clipboard and shows toast */
  async function copyTextToClipboard(): Promise<void> {
    try {
      await navigator.clipboard.writeText(textToCopy);
      openToast({
        title: successMessage,
      });
    } catch (error) {
      // eslint-disable-next-line no-console -- Useful to investigate issues with clipboard
      console.error("Failed to copy text: ", error);
      openToast({
        title: failedMessage,
        variant: "error",
      });
    }
  }

  return (
    <IconButton
      onClick={async () => {
        await copyTextToClipboard();
        if (trackEventName) {
          trackEvent(trackEventName);
        }
      }}
    >
      <ContentCopyIcon />
    </IconButton>
  );
}
